import { useState } from "react";
import FullScreenLayout from "../../../components/FullScreenLayout";
import { useAuth } from "../../../utils/context/AuthContext";
import { usePatients } from "../../../utils/context/PatientsContext";
import { ExpertRole } from "../../../utils/Enums";
import useGetChatSessions from "../hooks/useGetChatSessions";
import { hasUnreadMessages } from "../utils/unreadDetection";
import { ChatPreview } from "./ChatPreview";

export const ChatList = () => {
  const { auth } = useAuth()
  const role = auth.role
  const { patientsState } = usePatients()
  const patients = patientsState.patients
  const { data: chatSessions } = useGetChatSessions();
  const [searchTerm, setSearchTerm] = useState("");

  // Debug logs
  console.log('🔍 ChatList Debug:', {
    totalPatients: patients?.length,
    totalChatSessions: chatSessions?.length,
    searchTerm,
    role
  });

  // Apply filtering only if role is Admin or Specialist
  const filteredPatients = patients?.filter((patient) => {
    // First, check if this patient has a chat session
    const hasChatSession = chatSessions?.some(session => session.user_id === patient.user_id);
    
    // If patient has a chat session, always include them
    if (hasChatSession) {
      return true;
    }
    
    // For patients without chat sessions, apply role-based filtering
    // This allows creating new chats only with eligible patients
    if (role === ExpertRole.Admin) {
      return patient.sub_type !== null || patient.products?.includes("metabolic-hook");
    } else if (role === ExpertRole.Specialist) {
      return patient.sub_type !== null && 
             patient.sub_type !== "sub-metabolic-health" && 
             patient.sub_type !== "sub-metabolic-health-lite" && 
             !patient.products.includes("metabolic-hook");
    }
    
    return true; // For other roles, show all patients
  });

  console.log('🔍 After role filtering:', {
    filteredPatientsCount: filteredPatients?.length,
    unreadSessionUserId: '73946802-b071-7024-cd48-1d60017d6aa8' // The user_id from the unread session
  });

  const sortedPatients = filteredPatients?.slice().sort((a, b) => {
    const sessionA = chatSessions?.find((s) => s.user_id === a.user_id);
    const sessionB = chatSessions?.find((s) => s.user_id === b.user_id);

    const unreadA = sessionA ? hasUnreadMessages(sessionA) : false;
    const unreadB = sessionB ? hasUnreadMessages(sessionB) : false;

    if (unreadA && !unreadB) return -1;
    if (!unreadA && unreadB) return 1;

    const dateA = sessionA
      ? new Date(sessionA.last_message_sent_at).getTime()
      : 0;
    const dateB = sessionB
      ? new Date(sessionB.last_message_sent_at).getTime()
      : 0;

    return dateB - dateA;
  });

  console.log('🔍 After sorting:', {
    sortedPatientsCount: sortedPatients?.length,
    unreadPatients: sortedPatients?.filter(patient => {
      const session = chatSessions?.find(s => s.user_id === patient.user_id);
      return session ? hasUnreadMessages(session) : false;
    }).map(p => ({ id: p.user_id, name: `${p.user_name} ${p.user_family_name}` }))
  });

  const searchedPatients = sortedPatients?.filter((patient) => {
    const fullName = `${patient.user_name} ${patient.user_family_name}`.toLowerCase();
    return (
      fullName.includes(searchTerm.toLowerCase()) ||
      patient.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  console.log('🔍 After search filtering:', {
    searchedPatientsCount: searchedPatients?.length
  });

  return (
    <FullScreenLayout title="Chat">
      <div className='w-full flex flex-col space-y-6'>
        {/* Search and Stats */}
        <div className='w-full flex flex-col md:flex-row md:items-center md:justify-between gap-4'>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Cerca per nome o email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 px-3 py-2 rounded-md w-full md:w-[300px] text-sm"
              />
              <span className="text-sm text-gray-600 whitespace-nowrap">
                {searchedPatients?.length || 0} conversazioni
              </span>
            </div>
          </div>
        </div>
        
        {/* Chat List */}
        <div className='w-full space-y-4'>
          {
            searchedPatients && searchedPatients.map((patient, index) =>
              <ChatPreview patient={patient} key={index} />
            )
          }
        </div>
      </div>
    </FullScreenLayout>
  );
};
